<template>
  <el-card class="position-item" shadow="hover" @click="handleClick">
    <div class="position-header">
      <h3 class="position-title">{{ position.title }}</h3>
      <div class="position-priority" v-if="position.priority">
        <el-tag
          :type="getPriorityType(position.priority)"
          size="small"
          effect="plain"
        >
          {{ getPriorityLabel(position.priority) }}
        </el-tag>
      </div>
    </div>

    <div class="position-company">
      <span>{{ position.company?.name || '' }}</span>
    </div>

    <div class="position-details">
      <div class="position-salary" v-if="position.minSalary && position.maxSalary">
        {{ position.minSalary }}-{{ position.maxSalary }} {{ position.salaryCurrency || 'CNY' }}
      </div>
      
      <div class="position-work-type" v-if="position.workType">
        <el-tag size="small" effect="plain">{{ getWorkTypeLabel(position.workType) }}</el-tag>
      </div>
    </div>

    <div class="position-status">
      <application-status-tag :status="position.applicationStatus" size="small" />
      
      <div class="position-deadline" v-if="position.deadline">
        <el-tooltip content="截止日期" placement="top">
          <span><i class="el-icon-time"></i> {{ formatDate(position.deadline) }}</span>
        </el-tooltip>
      </div>
    </div>
  </el-card>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import type { PropType } from 'vue';
import type { Position } from '@/types/position';
import { WORK_TYPE_OPTIONS, PRIORITY_OPTIONS } from '@/types/position';
import ApplicationStatusTag from './ApplicationStatusTag.vue';
import { Calendar } from '@element-plus/icons-vue';

const props = defineProps({
  position: {
    type: Object as PropType<Position>,
    required: true
  }
});

const emit = defineEmits(['click']);

const handleClick = () => {
  emit('click', props.position);
};

const getWorkTypeLabel = (type: string) => {
  const option = WORK_TYPE_OPTIONS.find(opt => opt.value === type);
  return option ? option.label : type;
};

const getPriorityLabel = (priority: number) => {
  const option = PRIORITY_OPTIONS.find(opt => opt.value === priority);
  return option ? option.label : priority;
};

const getPriorityType = (priority: number) => {
  if (priority >= 4) return 'danger';
  if (priority >= 3) return 'warning';
  if (priority >= 2) return 'success';
  return 'info';
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('zh-CN', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit' 
  });
};
</script>

<style scoped>
.position-item {
  margin-bottom: 16px;
  cursor: pointer;
  transition: transform 0.2s;
}

.position-item:hover {
  transform: translateY(-3px);
}

.position-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.position-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.position-company {
  color: #606266;
  font-size: 14px;
  margin-bottom: 12px;
}

.position-details {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.position-salary {
  color: #67c23a;
  font-weight: 600;
}

.position-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.position-deadline {
  color: #909399;
  font-size: 12px;
  display: flex;
  align-items: center;
}

.position-deadline i {
  margin-right: 4px;
}
</style> 